import { Check, X } from "lucide-react"
import { cn } from "@/lib/cn"
import { wordDiff, type DiffSegment } from "@/lib/word-diff"

// Fields where `before`/`after` are long-form prose and a unified
// word-level diff is far more readable than two side-by-side panels.
const PROSE_FIELDS = new Set(["workflow_yaml", "description", "notes", "name", "version_name"])

const SNAPSHOT_FIELD_KEYS: Record<string, string> = {
  workflow_yaml: "workflowYaml",
  version_name: "versionName",
}

function snapshotFieldValue(snapshot: Record<string, unknown>, fieldKey: string): unknown {
  return snapshot[SNAPSHOT_FIELD_KEYS[fieldKey] ?? fieldKey]
}

export type Proposal = {
  id: string
  kind: string
  input: Record<string, unknown>
  /** Operator-side: has the operator ticked this proposal for apply? */
  accepted: boolean
  /** Per-skill monotonic counter assigned at persist time. Stable across
      apply/cancel so #34 always means "the 34th proposal for this skill". */
  number?: number
}

/**
 * What real-world target does this proposal touch? Two proposals with the
 * same target collapse into one card — the latest supersedes the earlier.
 * Forge sometimes re-proposes the same change across turns (e.g. the
 * operator says "all" twice and Forge drafts the diff twice). The audit
 * trail in test_runs.forge_chat keeps every version, but the approval
 * pane should only show one approvable card per target.
 */
export function proposalTargetKey(p: { kind: string; input: Record<string, unknown> }): string {
  switch (p.kind) {
    case "propose_field_update":           return `field:${String(p.input.field ?? "")}`
    case "propose_workflow_update":        return "workflow_yaml"
    case "propose_scenario_add":           return `scenario_add:${String(p.input.key ?? "")}`
    case "propose_scenario_update":        return `scenario:${String(p.input.scenario_id ?? "")}`
    case "propose_scenario_remove":        return `scenario:${String(p.input.scenario_id ?? "")}`
    default:                                return `${p.kind}:${JSON.stringify(p.input).slice(0, 64)}`
  }
}

/**
 * Upsert a proposal into a list keyed by target. If a prior proposal
 * already targets the same field/section/scenario/etc., the new one
 * REPLACES it in place (preserving slot order) so the operator sees a
 * single up-to-date card. Acceptance state on the existing card carries
 * forward — if the operator already toggled it, the refreshed proposal
 * stays in the same toggled state.
 */
export function upsertProposalByTarget(cur: Proposal[], incoming: Proposal): Proposal[] {
  const k = proposalTargetKey(incoming)
  const idx = cur.findIndex((p) => proposalTargetKey(p) === k)
  if (idx === -1) return [...cur, incoming]
  const next = [...cur]
  next[idx] = { ...incoming, accepted: cur[idx].accepted }
  return next
}

type Props = {
  proposal: Proposal
  onToggle: (id: string) => void
  onRemove: (id: string) => void
  /** The current persisted skill — used to render before/after diffs. */
  skillSnapshot: Record<string, unknown>
  /** Persisted scenarios — used to render the real before-state for scenario_update proposals. */
  scenarios?: Array<Record<string, unknown>>
}

const KIND_LABEL: Record<string, string> = {
  propose_field_update:           "Update field",
  propose_workflow_update:        "Update workflow",
  propose_scenario_add:           "Add scenario",
  propose_scenario_update:        "Update scenario",
  propose_scenario_remove:        "Remove scenario",
}

export function ProposalDiffCard({ proposal, onToggle, onRemove, skillSnapshot, scenarios = [] }: Props) {
  // [Recommended] tags are a chat-text construct ("Option A [Recommended]")
  // for ranking among alternatives. They have no meaning on a single
  // chosen-action proposal. Strip any leftover prefix from older rows
  // and from any future LLM slip — the card is the chosen action, period.
  const rawSummary = (proposal.input.summary as string | undefined) ?? ""
  const summary    = rawSummary.replace(/^\s*\[Recommended\]\s*[—–-]?\s*/i, "")
  const label      = KIND_LABEL[proposal.kind] ?? proposal.kind

  // Scenario kinds get an enriched header: which scenario (id + name)
  // and which scenario fields actually change. Without this, every
  // propose_scenario_update card just says "Update scenario" and the
  // operator can't tell from the header whether they're approving an
  // assertions rewrite, a name change, or a turn rewording.
  const isScenarioKind = proposal.kind === "propose_scenario_update" ||
                         proposal.kind === "propose_scenario_remove" ||
                         proposal.kind === "propose_scenario_add"
  let scenarioId: number | null = null
  let scenarioName = ""
  let scenarioFieldChips: string[] = []
  if (isScenarioKind) {
    if (proposal.kind === "propose_scenario_add") {
      const after = (proposal.input.after as Record<string, unknown> | undefined) ?? {}
      scenarioName = String(after.name ?? "")
      scenarioFieldChips = ["new"]
    } else {
      scenarioId = Number(proposal.input.scenario_id) || null
      const before = scenarios.find((s) => Number(s.id) === scenarioId) as Record<string, unknown> | undefined
      scenarioName = String(before?.name ?? "")
      if (proposal.kind === "propose_scenario_update") {
        const after = (proposal.input.after as Record<string, unknown> | undefined) ?? {}
        scenarioFieldChips = Object.keys(after)
          .filter((k) => !HIDDEN_SCENARIO_FIELDS.has(k))
          .filter((k) => !shallowEqual(before?.[k], after[k]))
          .map((k) => SCENARIO_FIELD_LABEL[k] ?? k)
      } else {
        scenarioFieldChips = ["remove"]
      }
    }
  }

  return (
    <div
      className={cn(
        "rounded-control border px-3 py-2 text-xs transition",
        proposal.accepted
          ? "border-vip-gold/40 bg-vip-goldMute"
          : "border-border bg-white/[0.03]",
      )}
    >
      <header className="mb-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => onToggle(proposal.id)}
            className={cn(
              "flex h-5 w-5 shrink-0 items-center justify-center rounded border transition",
              proposal.accepted
                ? "border-vip-gold/40 bg-vip-gold/20 text-vip-goldSoft"
                : "border-border-strong text-app-subtle hover:bg-white/5",
            )}
            aria-label={proposal.accepted ? "Reject proposal" : "Accept proposal"}
          >
            {proposal.accepted && <Check size={12} strokeWidth={3} />}
          </button>
          {proposal.number !== undefined && proposal.number !== null && (
            <span className="rounded-chip border border-border bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-app-subtle">
              #{proposal.number}
            </span>
          )}
          <span className="font-medium text-app-text">{label}</span>
          {(proposal.input.field as string | undefined) && (
            <code className="rounded bg-white/5 px-1 text-[10px] text-app-muted">
              {String(proposal.input.field)}
            </code>
          )}
          {(proposal.input.section as string | undefined) && (
            <code className="rounded bg-white/5 px-1 text-[10px] text-app-muted">
              ## {String(proposal.input.section)}
            </code>
          )}
          {(proposal.input.tool_key as string | undefined) && (
            <code className="rounded bg-white/5 px-1 text-[10px] text-app-muted">
              {String(proposal.input.tool_key)}
            </code>
          )}
          {isScenarioKind && scenarioId !== null && (
            <code className="rounded bg-white/5 px-1 text-[10px] text-app-muted">
              #{scenarioId}
            </code>
          )}
          {isScenarioKind && scenarioName && (
            <span className="text-[10px] text-app-muted truncate max-w-[200px]" title={scenarioName}>
              {scenarioName}
            </span>
          )}
          {isScenarioKind && scenarioFieldChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {scenarioFieldChips.map((c) => (
                <span key={c} className="rounded-chip border border-vip-gold/30 bg-vip-goldMute/40 px-1.5 py-0.5 text-[10px] text-vip-goldSoft">
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => onRemove(proposal.id)}
          className="text-app-subtle hover:text-app-text"
          aria-label="Discard proposal"
        >
          <X size={12} strokeWidth={2} />
        </button>
      </header>

      {summary && <p className="mb-1.5 text-app-muted">{summary}</p>}

      <Diff proposal={proposal} skillSnapshot={skillSnapshot} scenarios={scenarios} />
    </div>
  )
}

function Diff({ proposal, skillSnapshot, scenarios }: { proposal: Proposal; skillSnapshot: Record<string, unknown>; scenarios: Array<Record<string, unknown>> }) {
  const after = proposal.input.after
  const fieldKey = String(proposal.input.field ?? "")

  switch (proposal.kind) {
    case "propose_field_update": {
      const before = snapshotFieldValue(skillSnapshot, fieldKey)
      if (PROSE_FIELDS.has(fieldKey)) {
        return <ProseDiff before={typeof before === "string" ? before : ""} after={typeof after === "string" ? after : ""} />
      }
      return <SideBySide before={before} after={after} />
    }
    case "propose_workflow_update": {
      const before = (skillSnapshot.workflowYaml as string | undefined) ?? ""
      const afterYaml = String(proposal.input.after_yaml ?? after ?? "")
      return <ProseDiff before={before} after={afterYaml} />
    }
    case "propose_scenario_add":
      return <ScenarioFieldDiff before={null} after={after as Record<string, unknown> | null} />
    case "propose_scenario_update": {
      const sid = Number(proposal.input.scenario_id)
      const before = scenarios.find((s) => Number(s.id) === sid) ?? null
      return <ScenarioFieldDiff before={before} after={after as Record<string, unknown> | null} />
    }
    case "propose_scenario_remove":
      return <p className="text-app-muted">Detach scenario #{String(proposal.input.scenario_id)}</p>
    default:
      return <pre className="overflow-auto text-[10px] text-app-muted">{JSON.stringify(proposal.input, null, 2)}</pre>
  }
}

// Unified word-level prose diff. No before/after toggle — the
// "Current setup" tab already shows the persisted state, and the
// after-text is implicit in the diff (green + unchanged segments).
// Brand-new sections (empty before) render entirely as additions.
function ProseDiff({ before, after }: { before: string; after: string }) {
  const segments: DiffSegment[] = wordDiff(before, after)
  const isCreation = before.trim().length === 0
  const stats = isCreation ? null : countChanges(segments)

  return (
    <div className="text-[11px]">
      {stats && (stats.adds > 0 || stats.dels > 0) && (
        <div className="mb-1 flex items-center gap-2 text-[10px] text-app-subtle">
          {isCreation
            ? <span className="text-vip-goldSoft">new section</span>
            : <span>edits</span>}
          {stats.adds > 0  && <span className="text-status-success">+{stats.adds}</span>}
          {stats.dels > 0  && <span className="text-status-error">−{stats.dels}</span>}
        </div>
      )}
      <div className="max-h-64 overflow-auto whitespace-pre-wrap rounded border border-border bg-app-bg p-2 leading-relaxed text-app-text">
        {segments.length === 0 ? (
          <span className="text-app-muted">(no change)</span>
        ) : (
          segments.map((s, i) => <DiffChunk key={i} seg={s} />)
        )}
      </div>
    </div>
  )
}

function DiffChunk({ seg }: { seg: DiffSegment }) {
  if (seg.type === "equal") return <span className="text-app-muted">{seg.text}</span>
  if (seg.type === "add") {
    return (
      <span className="rounded bg-status-success/15 text-status-success">
        {seg.text}
      </span>
    )
  }
  return (
    <span className="rounded bg-status-error/15 text-status-error line-through decoration-status-error/60">
      {seg.text}
    </span>
  )
}

function countChanges(segs: DiffSegment[]): { adds: number; dels: number } {
  let adds = 0, dels = 0
  for (const s of segs) {
    if (s.type === "add") adds += s.text.replace(/\s+/g, " ").trim().split(" ").filter(Boolean).length
    else if (s.type === "del") dels += s.text.replace(/\s+/g, " ").trim().split(" ").filter(Boolean).length
  }
  return { adds, dels }
}

// Scenario diff renderer. Walks the proposed `after` object's top-level
// keys and only renders rows where the value actually changed. Each
// changed field gets a renderer matched to its type:
//   - text strings (persona, goal, attacker_instructions, name) → ProseDiff
//   - turns / assertions arrays                                  → JSON SideBySide (per-item structured)
//   - everything else                                            → SideBySide
//
// "before = null" means a brand-new scenario (propose_scenario_add).
// In that case every after-field renders as a creation (green-only).
//
// Fields the operator/system shouldn't see in the diff are filtered:
// scenario_id (routing only), id, created_at/updated_at.
const HIDDEN_SCENARIO_FIELDS = new Set([
  "scenario_id", "id", "created_at", "updated_at", "skill_id", "summary"
])
const TEXT_SCENARIO_FIELDS = new Set([
  "key", "name", "category", "scope", "persona", "goal",
  "attacker_instructions", "notes"
])

// Friendly chip labels for scenario top-level fields. `assertions` is
// the most-common source of header confusion ("am I editing the
// scenario or the assertions?") — show it explicitly so the operator
// can tell at a glance.
const SCENARIO_FIELD_LABEL: Record<string, string> = {
  name:                    "name",
  goal:                    "goal",
  persona:                 "persona",
  attacker_instructions:   "attacker turns",
  turns:                   "attacker turns",
  assertions:              "assertions",
  category:                "category",
  max_turns:               "max turns",
  key:                     "key",
  endpoint_mock_overrides: "mocks",
}
function ScenarioFieldDiff({
  before, after,
}: {
  before: Record<string, unknown> | null
  after:  Record<string, unknown> | null
}) {
  const a = after ?? {}
  const b = before ?? {}

  const keys = Object.keys(a).filter((k) => !HIDDEN_SCENARIO_FIELDS.has(k))
  const changedKeys = keys.filter((k) => !shallowEqual(b[k], a[k]))

  if (changedKeys.length === 0) {
    return <p className="text-[11px] text-app-subtle">No field changes.</p>
  }

  return (
    <div className="space-y-3">
      {changedKeys.map((k) => (
        <div key={k}>
          <div className="mb-0.5 text-[10px] uppercase tracking-wide text-app-subtle">{k}</div>
          {TEXT_SCENARIO_FIELDS.has(k) ? (
            <ProseDiff
              before={typeof b[k] === "string" ? (b[k] as string) : ""}
              after={typeof a[k] === "string"  ? (a[k] as string) : ""}
            />
          ) : (
            <SideBySide before={b[k] ?? null} after={a[k]} />
          )}
        </div>
      ))}
    </div>
  )
}

// Shallow-equal for diff filtering — compares JSON shape so arrays /
// objects with identical content don't show as "changed."
function shallowEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  try { return JSON.stringify(a) === JSON.stringify(b) } catch { return false }
}

function SideBySide({ before, after }: { before: unknown; after: unknown }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-[10px]">
      <div>
        <div className="mb-0.5 text-app-subtle">before</div>
        <pre className="max-h-32 overflow-auto rounded border border-border bg-app-bg p-1.5 text-app-muted">
{fmt(before)}
        </pre>
      </div>
      <div>
        <div className="mb-0.5 text-vip-goldSoft">after</div>
        <pre className="max-h-32 overflow-auto rounded border border-vip-gold/30 bg-vip-goldMute p-1.5 text-app-text">
{fmt(after)}
        </pre>
      </div>
    </div>
  )
}

function fmt(v: unknown): string {
  if (v == null) return "—"
  if (typeof v === "string") return v
  try {
    return JSON.stringify(v, null, 2)
  } catch {
    return String(v)
  }
}
